import { mergeStyleSets, IconButton } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import eventEmitter from "../../utilities/eventEmitter";
import { trackEvent } from "../../utilities/AppInsights";

interface VideoPlayerProps {
    videoUrl?: string;
    thumbnail?: string;
    className?: string; // Optional className prop
    isPlayingvalue?: boolean;
    id?: string;
    ismoblescreen?: boolean;
    section?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, thumbnail, className, isPlayingvalue = false, id, ismoblescreen = false, section }) => {
    const [isModalOpen, setIsModalOpen] = useState(isPlayingvalue);
    const [currentVideoUrl, setCurrentVideoUrl] = useState(videoUrl);
    const images = (require as any).context("../../assets", true);
    const defaultThumbnail = images("./people.png");
    const [thumbnailSrc, setThumbnailSrc] = useState(thumbnail || defaultThumbnail);

    const handleImageError = () => {
        setThumbnailSrc(defaultThumbnail);
    };
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isModalOpen]);
    // ✅ Detect if thumbnail image fails to load
    useEffect(() => {
        if (thumbnail) {
            const img = new Image();
            img.src = thumbnail;
            img.onload = () => setThumbnailSrc(thumbnail);
            img.onerror = () => setThumbnailSrc(defaultThumbnail);
        }
    }, [thumbnail]);
    useEffect(() => {
        const handlePlayVideo = ({ videoUrl, sourceComponent, id: eventId, isMobile }: { videoUrl: string; sourceComponent: string; id: string, isMobile: boolean }) => {
            if (sourceComponent === "SuccessStories" && eventId === id && isMobile === ismoblescreen) { // 🔹 Ensure only the matching video plays
                setCurrentVideoUrl(videoUrl);
                setIsModalOpen(true);
            }
        };

        eventEmitter.on("playVideo", handlePlayVideo);

        return () => {
            eventEmitter.off("playVideo", handlePlayVideo);
        };
    }, [id]); // 🔹 Depend on `id` to ensure each VideoPlayer handles its own events


    const styles = mergeStyleSets({
        videoContainer: {
            position: "relative",
            width: "100%",
            maxWidth: "600px",
            aspectRatio: "16/9",
            borderRadius: "16px",
            overflow: "hidden",
            "@media (max-width: 1024px)": {
                maxWidth: "100%",
                minHeight: "250px",
            },

        },
        thumbnailContainer: {
            width: "100%",
            height: "100%",
            backgroundImage: `url(${thumbnailSrc})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            cursor: "pointer",
            borderRadius: "16px",
            "@media (min-width: 1440px)": {
                backgroundSize: "cover",
            },

            "@media (min-width: 1920px)": {
                backgroundSize: "cover",
            },
        },
        thumbnailContainerMobile: {
            width: "100%",
            height: "100%",
            backgroundImage: `url(${thumbnailSrc})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            cursor: "pointer",
            "@media (max-width: 768px)": {
                minHeight: "450px",
            },
        },

        playButton: {
            background: "rgba(0, 0, 0, 0.7)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "60px",
            height: "60px",
            fontSize: "24px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "0.3s",
            "&:hover": {
                background: "rgba(0, 0, 0, 0.9)",
            },
        },
        modalOverlay: {
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
        },
        modalOverlayMobile: {
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999, // Ensure it's above all other elements
            overflow: "hidden", // Prevent scrolling of content below
        },

        modalContent: {
            position: "relative",
            width: "80%",
            maxWidth: "800px",
            aspectRatio: "16/9",
            backgroundColor: "#000",
            borderRadius: "16px",
            overflow: "hidden",
            // Full screen on mobile
            "@media (max-width: 768px)": {
                width: "100vw",
                height: "100vh",
                borderRadius: "0",
                maxWidth: "100%",
                maxHeight: "100%",
            },
        },

        closeButton: {
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "white",
            borderRadius: "50%",
            cursor: "pointer",
            fontSize: "20px",
        },
        iframe: {
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: "16px",
        },
    });

    return (
        <>
            <div className={className || styles.videoContainer}>
                <div
                    className={ismoblescreen ? styles.thumbnailContainerMobile : styles.thumbnailContainer}
                    onClick={() => { setIsModalOpen(true); trackEvent("VideoPlay", { VideoUrl: videoUrl, Section: section }) }}
                    onError={handleImageError} // Handle image loading errors
                >
                    <button className={styles.playButton}>▶</button>
                </div>
            </div>
            {isModalOpen && (
                <div className={ismoblescreen ? styles.modalOverlayMobile : styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                            iconProps={{ iconName: "Cancel" }}
                            className={styles.closeButton}
                            onClick={() => setIsModalOpen(false)}
                        />
                        <iframe
                            className={styles.iframe}
                            src={`${videoUrl}`}
                            allowFullScreen
                            allow="autoplay; encrypted-media"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default VideoPlayer;
